import type { ReactNode } from 'react';
import { CopyButton } from '@clarix/ui-shared';

function AppCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-border min-w-0 space-y-3 rounded-lg border p-3">
      <h3 className="text-foreground text-lg font-medium">{title}</h3>
      <div className="text-muted-foreground space-y-3 text-lg">{children}</div>
    </section>
  );
}

function SubStep({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <h4 className="text-foreground text-base font-medium">{title}</h4>
      {children}
    </div>
  );
}

/** A code block with its own header row, so the copy button never covers the code. */
function Snippet({
  children,
  caption,
  label,
}: {
  children: string;
  caption: string;
  label: string;
}) {
  return (
    <div className="bg-surface overflow-hidden rounded-md">
      <div className="border-border flex items-center justify-between border-b px-2.5 py-1">
        <span className="text-muted-foreground font-mono text-sm">{caption}</span>
        <CopyButton
          value={children}
          label={label}
          iconSize="h-4 w-4"
          toastOnCopy
          className="h-7 w-7"
        />
      </div>
      <pre className="text-foreground whitespace-pre-wrap break-all p-2.5 font-mono text-base">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function Steps({ steps }: { steps: readonly string[] }) {
  return (
    <ol className="list-decimal space-y-1 pl-5">
      {steps.map((step) => (
        <li key={step}>{step}</li>
      ))}
    </ol>
  );
}

const CHATGPT_STEPS = [
  'Open your profile menu → Settings → Plugins.',
  'Browse the directory, then choose Add → Add custom MCP server.',
  'Enter a name (for example "Clarix") and paste the connection URL below. Leave every other option at its default.',
  'Sign in with your Clarix login and click Allow.',
] as const;

/** Copy-paste setup for each supported AI app, filled in with this workspace's MCP URL (spec Part D). */
export function AiConnectorHelp({ mcpUrl }: { mcpUrl: string }) {
  const cursorConfig = JSON.stringify({ mcpServers: { clarix: { url: mcpUrl } } }, null, 2);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 lg:grid-cols-3">
        <AppCard title="Claude">
          <SubStep title="claude.ai and Claude Desktop">
            <p>Settings → Connectors → Add custom connector, then paste this URL.</p>
            <Snippet caption="Connector URL" label="connector URL">
              {mcpUrl}
            </Snippet>
          </SubStep>
          <SubStep title="Claude Code">
            <p>Run this in your terminal, then choose Authenticate under /mcp.</p>
            <Snippet caption="Terminal" label="Claude Code command">
              {`claude mcp add --transport http clarix ${mcpUrl}`}
            </Snippet>
          </SubStep>
        </AppCard>
        <AppCard title="ChatGPT">
          <Steps steps={CHATGPT_STEPS} />
          <Snippet caption="Connection URL" label="connection URL">
            {mcpUrl}
          </Snippet>
        </AppCard>
        <AppCard title="Cursor">
          <p>Add this to mcp.json, then sign in when Cursor asks.</p>
          <Snippet caption="mcp.json" label="Cursor config">
            {cursorConfig}
          </Snippet>
        </AppCard>
      </div>
      <p className="text-muted-foreground text-lg">
        You sign in with your Clarix login and click Allow; the app then acts as you in this
        workspace. Workspaces with an IP allowlist block claude.ai and ChatGPT, which connect from
        their own servers.
      </p>
    </div>
  );
}
